import { DirectDebitPaymentStatus, DirectDebitDdaStatus, DirectDebitCancellationStatus } from '../constants/directDebitEnums';

/**
 * Maps API status codes to PaymentStatus enum values
 * @param {string} apiCode - API status code (e.g., 'PNDG', 'ACCP')
 * @returns {string} PaymentStatus enum value
 */
export const mapPaymentStatusFromApiCode = (apiCode) => {
	if (!apiCode) return apiCode;
	const codeMap = {
		PNDG: DirectDebitPaymentStatus.Pending,
		APRP: DirectDebitPaymentStatus.UnderProcess,
		RPND: DirectDebitPaymentStatus.RepresentmentPending,
		ERR: DirectDebitPaymentStatus.Error,
		RJCT: DirectDebitPaymentStatus.Rejected,
		ACCP: DirectDebitPaymentStatus.Accepted,
	};
	return codeMap[apiCode] || apiCode;
};

/**
 * Maps PaymentStatus enum to API status code
 * @param {string} status - PaymentStatus enum value
 * @returns {string} API status code
 */
export const mapPaymentStatusToApiCode = (status) => {
	const statusMap = {
		[DirectDebitPaymentStatus.Pending]: 'PNDG',
		[DirectDebitPaymentStatus.UnderProcess]: 'APRP',
		[DirectDebitPaymentStatus.RepresentmentPending]: 'RPND',
		[DirectDebitPaymentStatus.Error]: 'ERR',
		[DirectDebitPaymentStatus.Rejected]: 'RJCT',
		[DirectDebitPaymentStatus.Accepted]: 'ACCP',
	};
	return statusMap[status] || status;
};

/**
 * Maps API status codes to DdaStatus enum values
 * @param {string} apiCode - API status code (e.g., 'PNDG', 'ACCP')
 * @returns {string} DdaStatus enum value
 */
export const mapDdaStatusFromApiCode = (apiCode) => {
	if (!apiCode) return apiCode;
	const codeMap = {
		PNDG: DirectDebitDdaStatus.Pending,
		DENY: DirectDebitDdaStatus.Denied,
		RQXP: DirectDebitDdaStatus.RequestExpired,
		SUBP: DirectDebitDdaStatus.PendingBankSubmission,
		APRP: DirectDebitDdaStatus.PendingBankApproval,
		ERR: DirectDebitDdaStatus.Error,
		RJCT: DirectDebitDdaStatus.Rejected,
		ACCP: DirectDebitDdaStatus.Active,
		SUSP: DirectDebitDdaStatus.Suspended,
		CANC: DirectDebitDdaStatus.Cancelled,
		EXPR: DirectDebitDdaStatus.Expired,
	};
	return codeMap[apiCode] || apiCode;
};

/**
 * Maps DdaStatus enum to API status code
 * @param {string} status - DdaStatus enum value
 * @returns {string} API status code
 */
export const mapDdaStatusToApiCode = (status) => {
	const statusMap = {
		[DirectDebitDdaStatus.Pending]: 'PNDG',
		[DirectDebitDdaStatus.Denied]: 'DENY',
		[DirectDebitDdaStatus.RequestExpired]: 'RQXP',
		[DirectDebitDdaStatus.PendingBankSubmission]: 'SUBP',
		[DirectDebitDdaStatus.PendingBankApproval]: 'APRP',
		[DirectDebitDdaStatus.Error]: 'ERR',
		[DirectDebitDdaStatus.Rejected]: 'RJCT',
		[DirectDebitDdaStatus.Active]: 'ACCP',
		[DirectDebitDdaStatus.Suspended]: 'SUSP',
		[DirectDebitDdaStatus.Cancelled]: 'CANC',
		[DirectDebitDdaStatus.Expired]: 'EXPR',
	};
	return statusMap[status] || status;
};

/**
 * Maps API status codes to CancellationStatus enum values
 * @param {string} apiCode - API status code (e.g., 'PNDG', 'ACCP')
 * @returns {string} CancellationStatus enum value
 */
export const mapCancellationStatusFromApiCode = (apiCode) => {
	const codeMap = {
		PNDG: DirectDebitCancellationStatus.Pending,
		ACCP: DirectDebitCancellationStatus.Accepted,
		RJCT: DirectDebitCancellationStatus.Rejected,
		ERR: DirectDebitCancellationStatus.Error,
	};
	return codeMap[apiCode] || apiCode;
};

/**
 * Maps CancellationStatus enum to API status code
 * @param {string} status - CancellationStatus enum value
 * @returns {string} API status code
 */
export const mapCancellationStatusToApiCode = (status) => {
	const statusMap = {
		[DirectDebitCancellationStatus.Pending]: 'PNDG',
		[DirectDebitCancellationStatus.Accepted]: 'ACCP',
		[DirectDebitCancellationStatus.Rejected]: 'RJCT',
		[DirectDebitCancellationStatus.Error]: 'ERR',
	};
	return statusMap[status] || status;
};

/**
 * Gets the color for a DDA status chip
 * @param {string} status - Status (can be API code or enum value)
 * @returns {string} MUI color name
 */
export const getDdaStatusColor = (status) => {
	if (!status) return 'default';
	// Handle both API codes and enum values
	const normalizedStatus = mapDdaStatusFromApiCode(status);
	
	const statusColors = {
		[DirectDebitDdaStatus.Pending]: 'warning',
		[DirectDebitDdaStatus.PendingBankSubmission]: 'warning',
		[DirectDebitDdaStatus.PendingBankApproval]: 'warning',
		[DirectDebitDdaStatus.Active]: 'success',
		[DirectDebitDdaStatus.Rejected]: 'error',
		[DirectDebitDdaStatus.Denied]: 'error',
		[DirectDebitDdaStatus.Cancelled]: 'default',
		[DirectDebitDdaStatus.Expired]: 'default',
		[DirectDebitDdaStatus.Error]: 'error',
		[DirectDebitDdaStatus.RequestExpired]: 'default',
		[DirectDebitDdaStatus.Suspended]: 'warning',
	};
	return statusColors[normalizedStatus] || 'default';
};

/**
 * Gets the color for a payment status chip
 * @param {string} status - Status (can be API code or enum value)
 * @returns {string} MUI color name
 */
export const getPaymentStatusColor = (status) => {
	if (!status) return 'default';
	// Handle both API codes and enum values
	const normalizedStatus = mapPaymentStatusFromApiCode(status);
	
	const statusColors = {
		[DirectDebitPaymentStatus.Pending]: 'warning',
		[DirectDebitPaymentStatus.UnderProcess]: 'warning',
		[DirectDebitPaymentStatus.RepresentmentPending]: 'warning',
		[DirectDebitPaymentStatus.Accepted]: 'success',
		[DirectDebitPaymentStatus.Rejected]: 'error',
		[DirectDebitPaymentStatus.Error]: 'error',
	};
	return statusColors[normalizedStatus] || 'default';
};
